import { programFrameSourceId } from '@ohmyprime/segmenter';
import type { GamingControlProvider, GamingSession } from '../net/api';
import type { components } from './contracts';

type Mapping = components['schemas']['SourceClockMapping'];

/** First-party bookkeeping only. The caller owns one deadline through capture, upload and receipt. */
export async function measureSourceClock(options: {
  provider: GamingControlProvider;
  session: GamingSession;
  track(): MediaStreamTrack | null;
  enabled: boolean;
  /** Must fence session/epoch/revision, scene, flag and publisher identity, including after ACK. */
  current(): boolean;
  /** Absolute performance clock deadline; do not restart the budget after this exchange. */
  deadlineMonotonicMs: number;
  now?: () => number;
}) {
  const { provider } = options;
  const session = { ...options.session };
  const track = options.track();
  const sourceId = track && programFrameSourceId(track);
  const now = options.now ?? (() => performance.now());
  const deadline = options.deadlineMonotonicMs;
  const initial = now();
  if (!options.enabled || session.state !== 'ENDING' || !provider.sourceChallenge || !track || !sourceId) {
    throw Error('source_clock_unavailable');
  }
  if (!Number.isFinite(initial) || !Number.isFinite(deadline) || deadline <= initial || deadline - initial > 5000) {
    throw Error('attempt_expired');
  }
  const current = () => {
    if (!options.current()) throw Error('attempt_changed');
    const time = now();
    if (!Number.isFinite(time) || time < initial || time >= deadline) throw Error('attempt_expired');
    if (options.track() !== track || track.readyState === 'ended' || programFrameSourceId(track) !== sourceId) {
      throw Error('source_changed');
    }
  };
  const bounded = async <T>(operation: () => Promise<T>): Promise<T> => {
    current();
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const result = await Promise.race([
        operation(),
        new Promise<never>((_, reject) => { timer = setTimeout(() => reject(Error('attempt_expired')), deadline - now()); }),
      ]);
      current();
      return result;
    } finally {
      clearTimeout(timer);
    }
  };
  const body = { epoch: session.epoch, revision: session.revision, sourceId };
  // A successful authenticated start/ACK confirms the server-owned finalTick prerequisite.
  const start = await bounded(() => provider.sourceChallenge!('startSourceChallenge', session.sessionId, session.streamId, body));
  // This instant is between the backend's start and ACK timestamps, never supplied by the game.
  const sourceMonotonicMs = now();
  const result = await bounded(() => provider.sourceChallenge!('ackSourceChallenge', session.sessionId, session.streamId, {
    ...body, challengeId: start.challengeId, sourceMonotonicMs,
  }));
  if (!('uncertaintyMs' in result) || result.challengeId !== start.challengeId || result.sourceMonotonicMs !== sourceMonotonicMs ||
      ![result.serverTimeMs, result.sourceMonotonicMs, result.expiresAtMs, result.uncertaintyMs].every(value =>
        Number.isFinite(value) && value >= 0 && !Object.is(value, -0)) || result.uncertaintyMs > 100) {
    throw Error('clock_uncertain');
  }
  const mapping: Mapping = Object.freeze({ ...result });
  const read = () => {
    current();
    const elapsed = now() - mapping.sourceMonotonicMs;
    if (!Number.isFinite(elapsed) || elapsed < 0 || mapping.serverTimeMs + elapsed + mapping.uncertaintyMs >= mapping.expiresAtMs) {
      throw Error('challenge_expired');
    }
    return mapping;
  };
  read();
  return { sourceId, read };
}
